import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Hard Rules & Caps modal — extracted from FlowBuilder unchanged.
// All state lives in the parent; this component only renders and calls back:
//   onAdd()              -> append a new blank rule
//   onUpdate(i, patch)   -> patch rule at index i (same as the old updR)
//   onRemove(i)          -> remove rule at index i
const inp = "h-9 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";

export default function RulesDialog({ open, onOpenChange, rules, meta, options, qIdOptions, onAdd, onUpdate, onRemove }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-sky bg-white sm:max-w-2xl" data-testid="flow-rules-dialog">
        <DialogHeader>
          <DialogTitle className="font-sora text-navy">Hard Rules & Caps</DialogTitle>
          <DialogDescription className="text-xs text-ink/50">Force or cap a lead's quality when answers match a set of conditions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={onAdd} data-testid="add-rule-button" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Plus className="h-3.5 w-3.5" />Add rule</button>
          </div>
          {rules.length === 0 && <p className="py-4 text-center text-sm text-ink/40">No hard rules.</p>}
          {rules.map((r, i) => (
            <div key={i} data-testid={`rule-${i}`} className="rounded-xl border border-sky/60 bg-mist/30 p-3">
              <div className="mb-2 flex items-center gap-2">
                <input className={`${inp} flex-1`} placeholder="Rule name" value={r.name} onChange={(e) => onUpdate(i, { name: e.target.value })} />
                <button onClick={() => onRemove(i)} className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mb-2 space-y-1.5">
                <p className="text-[11px] font-semibold text-ink/60">When ALL conditions match:</p>
                {(r.conditions || []).map((c, ci) => (
                  <div key={ci} className="flex flex-wrap items-center gap-1.5">
                    <select className="h-9 flex-1 rounded-lg border border-sky px-2 text-sm" value={c[0]} onChange={(e) => onUpdate(i, { conditions: r.conditions.map((x, xi) => xi === ci ? [e.target.value, x[1], x[2]] : x) })}>
                      <option value="">Select question</option>
                      {qIdOptions.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
                    </select>
                    <select className="h-9 rounded-lg border border-sky px-2 text-sm" value={c[1]} onChange={(e) => onUpdate(i, { conditions: r.conditions.map((x, xi) => xi === ci ? [x[0], e.target.value, x[2]] : x) })}>
                      {(meta?.rule_operators || []).map((op) => <option key={op} value={op}>{op}</option>)}
                    </select>
                    <input className="h-9 w-32 rounded-lg border border-sky px-2 text-sm" placeholder="value" value={c[2]} onChange={(e) => onUpdate(i, { conditions: r.conditions.map((x, xi) => xi === ci ? [x[0], x[1], e.target.value] : x) })} />
                    <button onClick={() => onUpdate(i, { conditions: r.conditions.filter((_, xi) => xi !== ci) })} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => onUpdate(i, { conditions: [...(r.conditions || []), ["", "equals", ""]] })} className="text-[11px] font-semibold text-loop hover:underline">+ condition</button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold text-ink/60">Then:</span>
                <select className="h-9 rounded-lg border border-sky px-2 text-sm" value={r.action} onChange={(e) => onUpdate(i, { action: e.target.value })}>
                  {(meta?.rule_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {["set_quality", "cap_quality"].includes(r.action) && (
                  <select className="h-9 rounded-lg border border-sky px-2 text-sm" value={r.target || ""} onChange={(e) => onUpdate(i, { target: e.target.value })}>
                    {(options?.qualities || meta?.qualities || []).map((qu) => <option key={qu} value={qu}>{qu}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
